"use client";

import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { IFilters } from "../../../interfaces/StockList.interface";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";
import { createDefaultStockFilters } from "../utils/stockListingHelpers";

type RawFilterValue = string | { key: number | string; displayLabel: string };

export type StockFilterCategoryKey = "alphabetical" | "exchange" | "sector" | "mktCapType";

export interface StockFilterCategory {
  key: StockFilterCategoryKey;
  name: string;
  values: RawFilterValue[];
}

export interface StockFiltersApiResponse {
  data: StockFilterCategory[];
  statusCode: number;
  status: string;
  message: string;
}

export type NormalizedFilterOption = { value: string; label: string };

export interface NormalizedFilterCategory {
  key: StockFilterCategoryKey;
  name: string;
  options: NormalizedFilterOption[];
}

export interface StockListingFiltersContextValue {
  filterCategories: NormalizedFilterCategory[];
  isLoading: boolean;
  error: string | null;
  activeFilters: IFilters;
  setActiveFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  clearAll: () => void;
  activeSortValue?: string;
  setActiveSortValue?: (value: string) => void;
  uiLabels: StockListingUILabels;
}

const DEFAULT_FILTERS: IFilters = createDefaultStockFilters();

const StockListingFiltersContext = createContext<StockListingFiltersContextValue | null>(null);

export function StockListingFiltersProvider({
  children,
  activeFilters,
  setActiveFilters,
  filterCategories: filterCategoriesProp,
  isLoadingFilters = false,
  activeSortValue,
  setActiveSortValue,
  uiLabels = {},
}: Readonly<{
  children: React.ReactNode;
  activeFilters: IFilters | undefined;
  setActiveFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  filterCategories?: NormalizedFilterCategory[];
  isLoadingFilters?: boolean;
  activeSortValue?: string;
  setActiveSortValue?: (value: string) => void;
  uiLabels?: StockListingUILabels;
}>) {
  const filterCategories = useMemo(
    () => filterCategoriesProp ?? [],
    [filterCategoriesProp],
  );

  const clearAll = useCallback(() => {
    setActiveFilters(createDefaultStockFilters());
  }, [setActiveFilters]);

  const value = useMemo<StockListingFiltersContextValue>(
    () => ({
      filterCategories,
      isLoading: isLoadingFilters,
      error: null,
      activeFilters: activeFilters ?? DEFAULT_FILTERS,
      setActiveFilters,
      clearAll,
      activeSortValue,
      setActiveSortValue,
      uiLabels,
    }),
    [
      filterCategories,
      isLoadingFilters,
      activeFilters,
      setActiveFilters,
      clearAll,
      activeSortValue,
      setActiveSortValue,
      uiLabels,
    ],
  );

  return (
    <StockListingFiltersContext.Provider value={value}>
      {children}
    </StockListingFiltersContext.Provider>
  );
}

export function useStockListingFilters() {
  const ctx = useContext(StockListingFiltersContext);
  if (!ctx) {
    // Allow usage in components (e.g. AEM table authoring wrappers) that may render
    // StockListing UI outside of StockListingFiltersProvider.
    // When outside the provider, there are no active filters.
    return {
      filterCategories: [],
      isLoading: false,
      error: null,
      activeFilters: DEFAULT_FILTERS,
      setActiveFilters: () => {
        // no-op outside provider
      },
      clearAll: () => {
        // no-op outside provider
      },
      activeSortValue: undefined,
      uiLabels: {},
    };
  }
  return ctx;
}
